/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component, Fragment } from "react";
import DatePicker from "react-datepicker";
import { NumericFormat } from "react-number-format";
import PhoneInput, { formatPhoneNumberIntl } from "react-phone-number-input";
import flags from "react-phone-number-input/flags";
import PropTypes from "prop-types";

import { simpleFetch } from "../../restWrapper";
import { isObjectEmpty, sortArrayByValue, capitalizeWords } from "../../assets/helper.js";
import { renderErrorMsg } from "../../assets/component/renderErrorMsg.js";
import hkRegions, { getDistrictsByRegion, getLabel, countryList } from "../../assets/hkAddressData.js";

import "./AddOfflinePayments.css";
import "react-datepicker/dist/react-datepicker.css";
import "react-phone-number-input/style.css";

import DefaultFormData from "./DefaultFormData";
import BulkUpload from "./BulkUpload";

class AddOfflinePayments extends Component {
  constructor(props) {
    super(props);

    if (
      this.props.location.state === null ||
      this.props.location.state === undefined
    ) {
      this.props.history.push("/OrgLogin");
    }

    this.state = {
      selectedMission: null,
      formData: [],
      showSendReceiptBtn: true,
      loading: false,
      errorMsg: {},
      errorMsgGlobal: '',
      mode: 'manual',
    };

    this.mapSelectedMissionFromProps = this.mapSelectedMissionFromProps.bind(this);
    this.mapFormData = this.mapFormData.bind(this);
    this.mapFieldsError = this.mapFieldsError.bind(this);

    this.handleBackBtn = this.handleBackBtn.bind(this);
    this.handleSaveButton = this.handleSaveButton.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleInputRemove = this.handleInputRemove.bind(this);
  }

  getPageText() {
    const { languageObj = {} } = this.props;
    if (languageObj.AddOfflinePayments) {
      return languageObj.AddOfflinePayments;
    }

    return languageObj;
  }

  componentDidMount() {
    this.setState({
      selectedMission: this.mapSelectedMissionFromProps(),
      formData: this.mapFormData(),
    });
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.formData !== this.state.formData &&
      this.state.formData.length
    ) {
      this.setState({
        errorMsg: this.mapFieldsError(),
      });
    }
  }

  mapSelectedMissionFromProps() {
    const { missions } = this.props.location.state;
    
    const selectedMission = missions.find((mission) => mission.clicked);

    return selectedMission || null;
  }

  mapFormData() {
    const { language } = this.props;
    const { organizationObj, missions } = this.props.location.state;
    const text = this.getPageText();

    const selectedMission = missions.find((mission) => mission.clicked);

    let newFormData = DefaultFormData(text);

    newFormData = newFormData.map((field) => {
      let value = field.value;
      let additionalData = {};
      let disabled = field.disabled || false;

      if (field.id === 'projectId') {
        if (selectedMission) {
          value = selectedMission.id;
        } else {
          value = 0; // set default value for quick payment
        }

        additionalData = {
          options: missions.map(mission => {
            let label = mission.title;
            if (mission.status === 'closed') {
              label += ` (${capitalizeWords(mission.status)})`;
            }
        
            return {
              label: label,
              value: mission.id,
              disabled: mission.status === 'closed',
            }
          }),
        }
        
        // add static value for quick payment
        additionalData.options.push({
          label: text.generalPayment || `General Payment`,
          value: 0,
          disabled: false,
        });
      }

      if (field.id === 'paymentType' && selectedMission) {
        value = selectedMission.donation ? 'donation' : 'payment';
        disabled = true;
      }

      return {
        ...field,
        value,
        ...additionalData,
        disabled,
      };
    });

    if (organizationObj.formSetting) {
      const getCustomFields = organizationObj.formSetting.rows || [];

      if (getCustomFields.length > 0) {        
        let customFields = sortArrayByValue(getCustomFields, 'order', 'asc');

        customFields = customFields.map((row, idx) => {
          const label = row.i18n.label[language] ? row.i18n.label[language] : row.i18n.label.en;
          const placeholder = row.i18n.placeholder[language] ? row.i18n.placeholder[language] : row.i18n.placeholder.en;

          let mappedField = {
            ...row,
            idx,
            label,
            placeholder,
            value: row.value || '',
            isCustomField: true,
            showOnReceipt: row.showOnReceipt !== undefined ? row.showOnReceipt : true,
            section: 'additional',
          };

          // Map dropdown options for custom select dropdown
          if (row.type === 'dropdown' && row.options) {
            mappedField.type = 'customDropdown';
            mappedField.options = row.options.map(opt => ({
              label: opt.label[language] || opt.label.en || '',
              value: opt.label[language] || opt.label.en || '',
            }));
          }

          // Default yesno to 'no'
          if (row.type === 'yesno') {
            mappedField.value = row.value || 'no';
          }

          // Default address to empty JSON
          if (row.type === 'address') {
            mappedField.value = row.value || '{}';
          }

          return mappedField;
        });

        customFields = customFields.filter((field) => !field.archived);

        newFormData = newFormData.concat(customFields);
      }
    }

    return newFormData
  }

  mapFieldsError() {
    const { organizationObj } = this.props.location.state;
    const { formData, errorMsg } = this.state;
    const text = this.getPageText();

    let err = errorMsg;

    // Check if payment amount is less than minimum amount
    const minimumAmount = organizationObj.minimumPaymentAmount || 0;
    const paymentAmount = formData.find((field) => field.id === "paymentAmount").value;

    if (paymentAmount < minimumAmount) {
      const template = text.minimumAmountError || `Minimum amount HK$${minimumAmount}`;
      err["paymentAmount"] = template.replace("{{amount}}", minimumAmount);
    }

    return err;
  }

  mapFieldsForSave(withSendReceipt) {
    const { language } = this.props;
    const { organization } = this.props.location.state;
    const { formData } = this.state;

    const findField = (id) => formData.find((field) => field.id === id).value;
    const findFieldFile = (id) => formData.find((field) => field.id === id).file;

    const newFormData = new FormData();
    newFormData.append('payerType', findField('payerType'));
    newFormData.append('firstName', findField('firstName'));
    newFormData.append('lastName', findField('lastName'));

    const payerType = findField('payerType');
    if (payerType === 'company') {
      newFormData.append('orgName', findField('orgName'));
    }
    newFormData.append('phoneNumber', findField('contact'));
    newFormData.append('paymentName', findField('paymentMethod'));
    newFormData.append('email', findField('email'));
    const amount = Number(findField('paymentAmount')) || 0;
    const transactionFee = Number(findField('transactionFee')) || 0;
    const net = Math.max(0, amount - transactionFee);
    newFormData.append('amount', amount);
    newFormData.append('transactionFee', transactionFee);
    newFormData.append('net', net);
    newFormData.append('currency', 'hkd');
    newFormData.append('language', language);
    newFormData.append('country', 'HK');
    newFormData.append('memo', findField('note'));
    newFormData.append('scheduledDate', findField('date'));

    // Send createdAt - use current time if "Use current time" is checked
    const createdAtField = formData.find((field) => field.id === 'createdAt');
    const createdAtValue = createdAtField && createdAtField.useNow
      ? new Date().toISOString()
      : findField('createdAt');
    newFormData.append('createdAt', createdAtValue);

    newFormData.append('status', findField('paymentStatus'));
    newFormData.append('proofofpayment', findFieldFile('proofPayment'));

    const paymentRefField = formData.find((field) => field.id === 'paymentReference');
    if (paymentRefField && paymentRefField.value) {
      newFormData.append('paymentReference', paymentRefField.value);
    }

    const projectId = findField('projectId');

    if (projectId === 0) {
      // For quick payment
      const projectName = `General ${capitalizeWords(findField('paymentType'))}`;

      newFormData.append('organizationId', organization._id);
      newFormData.append('organizationViewId', organization.id);
      newFormData.append('projectName', projectName);
      newFormData.append('transactionType', findField('paymentType'));
    } else {
      // Only send projectId for payments related to a mission
      newFormData.append('projectId', projectId);
    }

    formData.forEach((field) => {
      if (field.isCustomField) {
        newFormData.append(`fields[${field.idx}][id]`, field.id);
        newFormData.append(`fields[${field.idx}][value]`, field.value || '');
      }
    });

    if (withSendReceipt) {
      newFormData.append('sendReceipt', true);
    }

    return newFormData;
  }

  handleBackBtn() {
    this.props.history.push({
      pathname: "/AccountOverview",
      state: {
        selectedMission: this.state.selectedMission,
        isFetchPaymentsData: true,
      },
    });
  }

  handleSaveButton(withSendReceipt = false) {
    const { formData, errorMsg } = this.state;

    let err = errorMsg;

    const payerType = formData.find((f) => f.id === 'payerType');

    formData.forEach((field) => {
      // Skip hidden fields from validation
      if (field.hiddenWhenPayerType && payerType && payerType.value === field.hiddenWhenPayerType) {
        return;
      }

      if (field.required && field.value === "") {
        err[field.id] = "This field is required";
      }
    });

    this.setState({
      errorMsg: err,
    });

    if (isObjectEmpty(err)) {
      this.setState({
        loading: true,
        errorMsgGlobal: '',
      });

      const mappedFormData = this.mapFieldsForSave(withSendReceipt);

      simpleFetch('/organizations/payments/add', 'POST', mappedFormData, true).then(res => {
        if (res.body && res.body.id) {
          this.props.history.push({
            pathname: "/AccountOverview",
            state: {
              selectedMission: this.state.selectedMission,
              isFetchPaymentsData: true,
            },
          });
        } else if (res.body && res.body.message) {
          this.setState({
            errorMsgGlobal: res.body.message,
            loading: false,
          });
        } else {
          this.setState({
            errorMsgGlobal: 'Error creating payment',
            loading: false,
          });
        }
      }).catch(err => {
        this.setState({
          errorMsgGlobal: 'Network error while creating payment',
          loading: false,
        });
      });
    }
  }

  handleInputChange(event, field) {
    const { missions } = this.props.location.state;
    const { errorMsg } = this.state;
    const { value } = event.target;

    const files = event.target.files || null;

    this.setState((prevState) => {
      const updatedFormData = prevState.formData.map((data) => {
        if (data.id === field.id) {
          let newValue = value;
          let additionalData = {};

          if (field.type === "phone") {
            newValue = formatPhoneNumberIntl(value);
          }

          if (field.type === "datetime") {
            newValue = new Date(value).toISOString();
            additionalData = {
              valueObj: new Date(value),
            };
          }

          if (field.type === "decimal") {
            newValue = Number(value);
          }

          if (field.type === "file" && files) {
            const file = files[0];

            newValue = file.name;
            additionalData = {
              file,
            };
          }

          if (field.id === 'projectId') {
            newValue = parseInt(value, 10);
          }

          return {
            ...data,
            value: newValue,
            ...additionalData,
          };
        }

        return data;
      });

      // Calculate payment net amount if payment amount or transaction fee changes
      if (field.id === "paymentAmount" || field.id === "transactionFee") {
        const paymentAmount = field.id === "paymentAmount" 
          ? Number(value) 
          : Number(updatedFormData.find(data => data.id === "paymentAmount").value);
        
        const transactionFee = field.id === "transactionFee" 
          ? Number(value) 
          : Number(updatedFormData.find(data => data.id === "transactionFee").value);
        
        // Calculate net amount (payment amount - transaction fee)
        const netAmount = Math.max(0, paymentAmount - transactionFee);
        
        // Update the payment net amount field
        updatedFormData.forEach(data => {
          if (data.id === "paymentNetAmount") {
            data.value = netAmount;
          }
        });
      }

      let newShowSendReceiptBtn = prevState.showSendReceiptBtn;

      if (field.id === "paymentStatus" && value !== prevState.showSendReceiptBtn) {
        newShowSendReceiptBtn = !prevState.showSendReceiptBtn;
      }

      // conditional logic for donor type - toggle required fields
      if (field.id === 'payerType') {
        const isNonIndividual = value === 'company';

        updatedFormData.forEach(data => {
          if (data.id === 'firstName' || data.id === 'lastName') {
            data.required = !isNonIndividual;
          }
          if (data.id === 'orgName') {
            data.required = isNonIndividual;
          }
        });
      }

      // conditional logic for payment type based on selected project
      if (field.id === 'projectId') {
        const selectedProject = missions.find(data => data.id === parseInt(value, 10));

        // set default for quick payment
        let paymentTypeValue = 'payment';
        let paymentTypeDisabled = false;

        if (selectedProject) {
          paymentTypeValue = selectedProject.donation ? 'donation' : 'payment';
          paymentTypeDisabled = true;
        }

        // Update the payment type field
        updatedFormData.forEach(data => {
          if (data.id === "paymentType") {
            data.value = paymentTypeValue;
            data.disabled = paymentTypeDisabled;
          }
        });
      }

      let err = errorMsg;
      delete err[field.id];

      return {
        formData: updatedFormData,
        showSendReceiptBtn: newShowSendReceiptBtn,
        errorMsg: err,
      };
    });
  }

  handleInputRemove(field) {
    const { errorMsg } = this.state;

    this.setState((prevState) => {
      const updatedFormData = prevState.formData.map((data) => {
        if (data.id === field.id) {
          let additionalData = {};

          if (field.type === "file") {
            additionalData = {
              file: null,
            };
          }

          return {
            ...data,
            value: "",
            ...additionalData,
          };
        }

        return data;
      });

      let err = errorMsg;
      delete err[field.id];

      return {
        formData: updatedFormData,
        errorMsg: err,
      };
    });
  }

  renderHeader() {
    const { showSendReceiptBtn, errorMsg } = this.state;
    const text = this.getPageText();

    const isDisabled = !isObjectEmpty(errorMsg);

    return (
      <div className="AddOfflinePayments-header">
        <div className="AddOfflinePayments-headerLeftContainer">
          <div
            className="AddOfflinePayments-backBtnContainer"
            onClick={this.handleBackBtn}
          >
            <div className="AddOfflinePayments-backBtn"></div>
            <div className="AddOfflinePayments-backText">
              {text.back || "Back"}
            </div>
          </div>

          <div className="AddOfflinePayments-headerTitle">
            {text.title || "Add Offline Payments"}
          </div>
        </div>

        <div className="AddOfflinePayments-headerRightContainer">
          <div
            data-testid="aop-save-btn"
            className={`AddOfflinePayments-saveBtnContainer ${isDisabled ? "disabled" : ""}`}
            onClick={() => this.handleSaveButton()}
          >
            <div className="AddOfflinePayments-saveBtn"></div>
            <div className="AddOfflinePayments-saveText">
              {text.saveOnly || "Save only"}
            </div>
          </div>

          {showSendReceiptBtn && (
            <div
              className={`AddOfflinePayments-saveSendBtnContainer ${isDisabled ? "disabled" : ""}`}
              onClick={() => this.handleSaveButton(true)}
            >
              <div className="AddOfflinePayments-saveSendBtn"></div>
              <div className="AddOfflinePayments-saveSendText">
                {text.saveAndSend || "Save and Send Receipt"}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  renderGlobalError() {
    const { errorMsgGlobal } = this.state;

    if (errorMsgGlobal !== '') {
      return (
        <div className="AddOfflinePayments-globalError">
          {renderErrorMsg(errorMsgGlobal)}
        </div>
      );
    }

    return null;
  }

  renderError(field) {
    const { errorMsg } = this.state;

    if (field.id === "paymentAmount") {
      return this.renderErrorPaymentAmount();
    }

    if (errorMsg[field.id]) {
      return renderErrorMsg(errorMsg[field.id]);
    }

    return null;
  }

  renderErrorPaymentAmount() {
    const { organizationObj } = this.props.location.state;
    const { formData } = this.state;
    const text = this.getPageText();

    const minimumAmount = organizationObj.minimumPaymentAmount || 0;

    const paymentAmount = formData.find((field) => field.id === "paymentAmount").value;

    if (paymentAmount < minimumAmount) {
      const template = text.minimumAmountError || `Minimum amount HK$${minimumAmount}`;
      return renderErrorMsg(template.replace("{{amount}}", minimumAmount));
    }

    return null;
  }

  renderField(data) {
    // Hide fields based on donor type
    if (data.hiddenWhenPayerType) {
      const payerType = this.state.formData.find((field) => field.id === 'payerType');
      if (payerType && payerType.value === data.hiddenWhenPayerType) {
        return null;
      }
    }

    if (data.type === "toggle") {
      return this.renderFieldToggle(data);
    } else if (data.type === "text") {
      return this.renderFieldText(data);
    } else if (data.type === "textarea") {
      return this.renderFieldTextarea(data);
    } else if (data.type === "decimal") {
      return this.renderFieldDecimal(data);
    } else if (data.type === "phone") {
      return this.renderFieldPhone(data);
    } else if (data.type === "datetime") {
      return this.renderFieldDatetime(data);
    } else if (data.type === "customDropdown") {
      return this.renderFieldCustomDropdown(data);
    } else if (data.type === "dropdown") {
      return this.renderFieldDropdown(data);
    } else if (data.type === "file") {
      return this.renderFieldFile(data);
    } else if (data.type === "readonly") {
      return this.renderFieldReadonly(data);
    } else if (data.type === "yesno") {
      return this.renderFieldYesNo(data);
    } else if (data.type === "address") {
      return this.renderFieldAddress(data);
    }

    return null;
  }

  renderFieldToggle(data) {
    return (
      <div key={data.id} data-testid={`aop-field-${data.id}`} className="AddOfflinePayments-inputContainer">
        <div className="AddOfflinePayments-subheading">
          {data.label}
        </div>

        <div className="AddOfflinePayments-toggleSwitch">
          {data.options.map((option) => (
            <div
              key={option.value}
              className={`AddOfflinePayments-toggleOption${data.value === option.value ? ' AddOfflinePayments-toggleOption--active' : ''}`}
              onClick={() => this.handleInputChange({ target: { value: option.value } }, data)}
            >
              {option.label}
            </div>
          ))}
        </div>
      </div>
    );
  }

  renderFieldCustomDropdown(data) {
    const options = data.options || [];
    return (
      <div key={data.id} data-testid={`aop-field-${data.id}`} className="AddOfflinePayments-inputContainer">
        <div className="AddOfflinePayments-subheading">
          {data.label}
          {data.showOnReceipt && (
            <div className="AddOfflinePayments-smallreceiptIcn"></div>
          )}
        </div>

        <div className="AddOfflinePayments-inputFieldContainer" style={{ marginBottom: '8px' }}>
          <select
            className="AddOfflinePayments-inputField"
            value={data.value || ''}
            onChange={(e) => this.handleInputChange({ target: { value: e.target.value } }, data)}
          >
            <option value="">{this.getPageText().select || '-- Select --'}</option>
            {options.map((opt, idx) => (
              <option key={idx} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {this.renderError(data)}
      </div>
    );
  }

  renderFieldYesNo(data) {
    const isYes = data.value === 'yes';
    return (
      <div key={data.id} data-testid={`aop-field-${data.id}`} className="AddOfflinePayments-inputContainer">
        <div className="AddOfflinePayments-subheading">
          {data.label}
          {data.showOnReceipt && (
            <div className="AddOfflinePayments-smallreceiptIcn"></div>
          )}
        </div>

        <div
          className="AddOfflinePayments-yesNoToggle"
          onClick={() => {
            const newVal = isYes ? 'no' : 'yes';
            this.handleInputChange({ target: { value: newVal } }, data);
          }}
        >
          <div className={`AddOfflinePayments-yesNoOption${isYes ? ' AddOfflinePayments-yesNoOption--active' : ''}`}>
            {this.props.language === 'zh' || this.props.language === 'cn' ? '是' : 'Yes'}
          </div>
          <div className={`AddOfflinePayments-yesNoOption${!isYes ? ' AddOfflinePayments-yesNoOption--active' : ''}`}>
            {this.props.language === 'zh' || this.props.language === 'cn' ? '否' : 'No'}
          </div>
        </div>

        {this.renderError(data)}
      </div>
    );
  }

  renderFieldAddress(data) {
    const { language } = this.props;
    let parsed = {};
    try { parsed = JSON.parse(data.value || '{}'); } catch (e) { parsed = {}; }

    const isHK = (parsed.country || 'Hong Kong') === 'Hong Kong';
    const districts = parsed.region ? getDistrictsByRegion(parsed.region) : [];
    const addr = (this.getPageText() && this.getPageText().address) || {};

    const handleSubChange = (subKey, subValue) => {
      let updated;
      if (subKey === 'country') {
        updated = { country: subValue };
      } else if (subKey === 'region') {
        updated = { ...parsed, region: subValue, district: '' };
      } else {
        updated = { ...parsed, [subKey]: subValue };
      }
      this.handleInputChange({ target: { value: JSON.stringify(updated) } }, data);
    };

    const intlFields = [
      { key: 'address1', placeholder: addr.addressLine1 || 'Address Line 1' },
      { key: 'address2', placeholder: addr.addressLine2 || 'Address Line 2' },
      { key: 'city', placeholder: addr.city || 'City' },
      { key: 'state', placeholder: addr.state || 'State / Province' },
      { key: 'postalCode', placeholder: addr.postalCode || 'Postal Code' },
    ];

    const hkTextFields = [
      { key: 'street', placeholder: addr.street || 'Street No. & Name' },
      { key: 'building', placeholder: addr.building || 'Building Name' },
      { key: 'block', placeholder: addr.block || 'Block / Tower' },
      { key: 'floor', placeholder: addr.floor || 'Floor' },
      { key: 'flat', placeholder: addr.flat || 'Flat / Unit' },
    ];

    return (
      <div key={data.id} data-testid={`aop-field-${data.id}`} className="AddOfflinePayments-inputContainer">
        <div className="AddOfflinePayments-subheading">
          {data.label}
          {data.showOnReceipt && (
            <div className="AddOfflinePayments-smallreceiptIcn"></div>
          )}
        </div>

        <div className="AddOfflinePayments-inputFieldContainer" style={{ marginBottom: '8px' }}>
          <select
            className="AddOfflinePayments-inputField"
            value={parsed.country || 'Hong Kong'}
            onChange={(e) => handleSubChange('country', e.target.value)}
          >
            {countryList.map(c => (
              <option key={c.value} value={c.value}>{getLabel(c, language)}</option>
            ))}
          </select>
        </div>

        {isHK ? (
          <Fragment>
            <div className="AddOfflinePayments-inputFieldContainer" style={{ marginBottom: '8px' }}>
              <select
                className="AddOfflinePayments-inputField"
                value={parsed.region || ''}
                onChange={(e) => handleSubChange('region', e.target.value)}
              >
                <option value="">{addr.selectRegion || "-- Select Region --"}</option>
                {hkRegions.map(r => (
                  <option key={r.id} value={r.id}>{getLabel(r, language)}</option>
                ))}
              </select>
            </div>

            <div className="AddOfflinePayments-inputFieldContainer" style={{ marginBottom: '8px' }}>
              <select
                className="AddOfflinePayments-inputField"
                value={parsed.district || ''}
                onChange={(e) => handleSubChange('district', e.target.value)}
                disabled={!parsed.region}
              >
                <option value="">{addr.selectDistrict || "-- Select District --"}</option>
                {districts.map(d => (
                  <option key={d.id} value={d.id}>{getLabel(d, language)}</option>
                ))}
              </select>
            </div>

            {hkTextFields.map(sub => (
              <div key={sub.key} className="AddOfflinePayments-inputFieldContainer" style={{ marginBottom: '8px' }}>
                <input
                  type="text"
                  className="AddOfflinePayments-inputField"
                  value={parsed[sub.key] || ''}
                  onChange={(e) => handleSubChange(sub.key, e.target.value)}
                  placeholder={sub.placeholder}
                />
              </div>
            ))}
          </Fragment>
        ) : (
          <Fragment>
            {intlFields.map(sub => (
              <div key={sub.key} className="AddOfflinePayments-inputFieldContainer" style={{ marginBottom: '8px' }}>
                <input
                  type="text"
                  className="AddOfflinePayments-inputField"
                  value={parsed[sub.key] || ''}
                  onChange={(e) => handleSubChange(sub.key, e.target.value)}
                  placeholder={sub.placeholder}
                />
              </div>
            ))}
          </Fragment>
        )}

        {this.renderError(data)}
      </div>
    );
  }

  renderFieldReadonly(data) {
    return (
      <div key={data.id} data-testid={`aop-field-${data.id}`} className="AddOfflinePayments-inputContainer">
        <div className="AddOfflinePayments-subheading">
          {data.label}
        </div>
        <div className="AddOfflinePayments-inputFieldContainer">
          <input
            type="text"
            className="AddOfflinePayments-inputField"
            value={data.value}
            readOnly
            disabled
            style={{ backgroundColor: '#f5f5f5', color: '#888' }}
          />
        </div>
      </div>
    );
  }

  renderFieldText(data) {
    return (
      <div key={data.id} data-testid={`aop-field-${data.id}`} className="AddOfflinePayments-inputContainer">
        <div className="AddOfflinePayments-subheading">
          {data.label}
          {data.showOnReceipt && (
            <div className="AddOfflinePayments-smallreceiptIcn"></div>
          )}
        </div>

        <div className="AddOfflinePayments-inputFieldContainer">
          <input
            type="text"
            className="AddOfflinePayments-inputField"
            value={data.value}
            placeholder={data.placeholder}
            onChange={(event) => this.handleInputChange(event, data)}
          />
        </div>

        {this.renderError(data)}
      </div>
    );
  }

  renderFieldTextarea(data) {
    return (
      <div key={data.id} data-testid={`aop-field-${data.id}`} className="AddOfflinePayments-inputContainer">
        <div className="AddOfflinePayments-subheading">
          {data.label}
          {data.showOnReceipt && (
            <div className="AddOfflinePayments-smallreceiptIcn"></div>
          )}
        </div>

        <div className="AddOfflinePayments-textareaFieldContainer">
          <textarea
            type="text"
            className="AddOfflinePayments-textareaField"
            value={data.value}
            placeholder={data.placeholder}
            onChange={(event) => this.handleInputChange(event, data)}
          />
        </div>

        {this.renderError(data)}
      </div>
    );
  }

  renderFieldDecimal(data) {
    const classDisabled = data.disabled ? 'disabled' : '';

    return (
      <div key={data.id} data-testid={`aop-field-${data.id}`} className="AddOfflinePayments-inputContainer">
        <div className="AddOfflinePayments-subheading">
          {data.label}
          {data.showOnReceipt && (
            <div className="AddOfflinePayments-smallreceiptIcn"></div>
          )}
        </div>

        <div
          className={`AddOfflinePayments-inputFieldContainer ${classDisabled}`}
        >
          <div className="AddOfflinePayments-currencySign">HKD</div>
          <NumericFormat
            pattern="[0-9]*"
            inputMode="decimal"
            decimalSeparator="."
            className={`AddOfflinePayments-dollarField ${classDisabled}`}
            valueIsNumericString={true}
            value={data.value}
            onValueChange={(values) => this.handleInputChange({ target: { value: values.value } }, data)}
            allowNegative={false}
            thousandSeparator={true}
            prefix={"$"}
            fixedDecimalScale={true}
            decimalScale={2}
            disabled={data.disabled}
          />
        </div>

        {this.renderError(data)}
      </div>
    );
  }

  renderFieldPhone(data) {
    return (
      <div key={data.id} data-testid={`aop-field-${data.id}`} className="AddOfflinePayments-inputContainer">
        <div className="AddOfflinePayments-subheading">
          {data.label}
          {data.showOnReceipt && (
            <div className="AddOfflinePayments-smallreceiptIcn"></div>
          )}
        </div>

        <div className="AddOfflinePayments-inputFieldContainer">
          <div className="AddOfflinePayments-phoneField">
            <PhoneInput
              placeholder={data.placeholder}
              value={data.value}
              defaultCountry={"HK"}
              flags={flags}
              onChange={(phone) => this.handleInputChange({ target: { value: phone } }, data)}
            />
          </div>
        </div>

        {this.renderError(data)}
      </div>
    );
  }

  renderFieldDatetime(data) {
    const isUseNow = data.useNow === true;

    return (
      <div key={data.id} data-testid={`aop-field-${data.id}`} className="AddOfflinePayments-inputContainer">
        <div className="AddOfflinePayments-subheading" style={data.id === 'createdAt' ? { display: 'flex', alignItems: 'center', gap: '12px' } : {}}>
          <span>
            {data.label}
            {data.showOnReceipt && (
              <div className="AddOfflinePayments-smallreceiptIcn"></div>
            )}
          </span>
          {data.id === 'createdAt' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '400', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <input
                type="checkbox"
                checked={isUseNow}
                onChange={() => {
                  const now = new Date();
                  this.setState((prevState) => ({
                    formData: prevState.formData.map((field) =>
                      field.id === 'createdAt'
                        ? { ...field, useNow: !isUseNow, value: now.toISOString(), valueObj: now }
                        : field
                    ),
                  }));
                }}
              />
              {this.getPageText().useCurrentTime || "Use current time"}
            </label>
          )}
        </div>

        <div className="AddOfflinePayments-inputFieldContainer">
          <DatePicker
            className="AddOfflinePayments-inputField"
            placeholderText="YYYY/MM/DD H:MM"
            selected={data.valueObj}
            onChange={(date) => this.handleInputChange({ target: { value: date } }, data)}
            showTimeInput
            timeInputLabel="Time:"
            dateFormat="yyyy/MM/dd h:mm aa"
            disabled={isUseNow}
          />
        </div>

        {this.renderError(data)}
      </div>
    );
  }

  renderFieldDropdown(data) {
    const options = data.options || [];
    let selected = options[0] || { label: '', value: '' };

    if (data.value !== null && data.value !== undefined && data.value !== '') {
      selected = options.find((option) => option.value === data.value) || selected;
    }

    const classDisabled = data.disabled ? 'disabled' : '';

    return (
      <div key={data.id} data-testid={`aop-field-${data.id}`} className="AddOfflinePayments-inputContainer">
        <div className="AddOfflinePayments-subheading">
          {data.label}
          {data.showOnReceipt && (
            <div className="AddOfflinePayments-smallreceiptIcn"></div>
          )}
        </div>

        <div
          className={`AddOfflinePayments-inputFieldContainer ${classDisabled}`}
        >
          <div
            className={`AddOfflinePayments-formDropdownBtn ${classDisabled}`}
          >
            <div className="AddOfflinePayments-formDropdownText">
              {selected.label}
            </div>
            <div className="AddOfflinePayments-formDropdownIcn" />
            <select
              className={`AddOfflinePayments-formDropdownSelect ${classDisabled}`}
              value={selected.value}
              onChange={(event) => this.handleInputChange(event, data)}
              disabled={data.disabled}
            >
              {data.options.map((option) => (
                <option key={option.value} value={option.value} disabled={option.disabled || false}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {this.renderError(data)}
      </div>
    );
  }

  renderFieldFile(data) {
    const text = this.getPageText();
    const uploadedTextTemplate = text.uploadSuccess || "Document successfully uploaded: {{filename}}";

    const uploadView = (
      <Fragment>
        <div className="AddOfflinePayments-uploadIcon" />
        <input
          type="file"
          ref={(input) => (this.fileInput = input)}
          accept=".pdf,.jpg,.jpeg,.png"
          style={{ display: "none" }}
          onChange={(event) => this.handleInputChange(event, data)}
        />
        <button 
          className="AddOfflinePayments-uploadBtn" 
          onClick={() => this.fileInput.click()}
        >
          {data.placeholder}
        </button>
      </Fragment>
    );

    const fileView = (
      <Fragment>
        {uploadedTextTemplate.replace("{{filename}}", data.value)}
        <button
          className="AddOfflinePayments-removeFileBtn"
          onClick={() => this.handleInputRemove(data)}
        >
          {text.remove || "Remove"}
        </button>
      </Fragment>
    );

    return (
      <div key={data.id} data-testid={`aop-field-${data.id}`} className="AddOfflinePayments-inputContainer">
        <div className="AddOfflinePayments-subheading">
          {data.label}
          {data.showOnReceipt && (
            <div className="AddOfflinePayments-smallreceiptIcn"></div>
          )}
        </div>

        <div className="AddOfflinePayments-fileFieldContainer">
          <div className="AddOfflinePayments-fileField">
            {data.value && data.file ? fileView : uploadView}
          </div>
        </div>

        {this.renderError(data)}
      </div>
    );
  }

  renderModeTabs(bulkEnabled) {
    const { mode } = this.state;
    const bulkActive = mode === 'bulk' && bulkEnabled;
    const text = this.getPageText();
    const bulkBlock = (text && text.BulkUpload) || {};
    return (
      <div className="AddOfflinePayments-modeTabs">
        <button
          type="button"
          className={"AddOfflinePayments-modeTab " + (mode === 'manual' ? 'is-active' : '')}
          onClick={() => this.setState({ mode: 'manual' })}
        >
          {text.modeManual || "Manual entry"}
        </button>
        <button
          type="button"
          className={
            "AddOfflinePayments-modeTab " +
            (bulkActive ? 'is-active ' : '') +
            (bulkEnabled ? '' : 'is-locked')
          }
          onClick={() => bulkEnabled && this.setState({ mode: 'bulk' })}
          disabled={!bulkEnabled}
          title={bulkEnabled ? '' : (text.bulkLockTooltip || 'Upgrade to Premium to unlock bulk upload')}
        >
          {text.modeBulk || "Bulk upload"}
          <span className="AddOfflinePayments-betaBadge">{bulkBlock.betaBadge || "Beta"}</span>
          {!bulkEnabled && (
            <span className="AddOfflinePayments-premiumBadge">{text.premiumBadge || "Premium"}</span>
          )}
        </button>
      </div>
    );
  }

  render() {
    const { formData, mode } = this.state;
    const text = this.getPageText();

    if (
      this.state.loading ||
      this.props.location.state === null ||
      this.props.location.state === undefined
    ) {
      return (
        <div className="AddOfflinePayments-emptyPage">
          <div className="AddOfflinePayments-center">
            <div className="AddOfflinePayments-btm-load"></div>
          </div>
        </div>
      );
    }

    const { organizationObj, missions } = this.props.location.state;
    const bulkEnabled =
      !!organizationObj && organizationObj.currentPlan === 'premium';

    if (mode === 'bulk' && bulkEnabled) {
      return (
        <div className="AddOfflinePayments-view">
          <div className="AddOfflinePayments-prompt">
            {this.renderHeader()}
            {this.renderModeTabs(bulkEnabled)}
            <BulkUpload
              organizationObj={organizationObj}
              missions={missions}
              language={this.props.language}
              languageObj={text}
            />
          </div>
        </div>
      );
    }

    const payerTypeField = formData.find((data) => data.id === 'payerType');
    const sectionFields = (sectionName) => formData.filter((data) => data.section === sectionName);

    const sections = [
      { key: 'payment', title: text.sectionPayment || 'Payment Details' },
      { key: 'user', title: text.sectionUser || 'Payer Details' },
      { key: 'additional', title: text.sectionAdditional || 'Additional Details' },
    ];

    return (
      <div className="AddOfflinePayments-view">
        <div className="AddOfflinePayments-prompt">
          {this.renderHeader()}

          {this.renderModeTabs(bulkEnabled)}

          {payerTypeField && (
            <div className="AddOfflinePayments-userTypeContainer">
              {this.renderFieldToggle(payerTypeField)}
            </div>
          )}

          <div className="AddOfflinePayments-contentContainer">
            {this.renderGlobalError()}

            {sections.map((section) => {
              const fields = sectionFields(section.key);
              if (fields.length === 0) return null;

              return (
                <div key={section.key} className="AddOfflinePayments-section">
                  <div className="AddOfflinePayments-sectionTitle">
                    {section.title}
                  </div>
                  <div className="AddOfflinePayments-formContainer">
                    {fields.map((data) => this.renderField(data))}
                  </div>
                </div>
              );
            })}

            <div className="AddOfflinePayments-footerContainer">
              <div className="AddOfflinePayments-infoContainer">
                <div className="AddOfflinePayments-infoIcn" />
                <div className="AddOfflinePayments-infoText">
                  {text.receiptInfo || "Fields with a receipt icon will appear on the receipt."}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

AddOfflinePayments.propTypes = {
  language: PropTypes.string,
  languageObj: PropTypes.object,
  // i18n: PropTypes.object,
  state: PropTypes.object,
};

export default AddOfflinePayments;
